import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import type {
  MerchantCategory,
  MerchantCategoryRelationship,
} from "~/lib/types";

type CategoryGroup = {
  parent: MerchantCategory;
  children: MerchantCategory[];
};

type Props = {
  categories: MerchantCategory[];
  relationships: MerchantCategoryRelationship[];
  className?: string;
};

export const CategoryFilter = ({
  categories,
  relationships,
  className = "",
}: Props) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedCategoryId = searchParams.get("category") ?? "";

  // Build parent→children groups
  const groups = useMemo((): CategoryGroup[] => {
    const parentIds = new Set(relationships.map(r => r.related_item_id));
    const categoryById = new Map(categories.map(c => [c.id, c]));

    const parentMap = new Map(
      categories.filter(c => parentIds.has(c.id)).map(c => [c.id, c]),
    );

    const childrenByParent = relationships.reduce((acc, rel) => {
      const child = categoryById.get(rel.item_id);
      if (!child) return acc;
      const existing = acc.get(rel.related_item_id) ?? [];
      return new Map(acc).set(rel.related_item_id, [...existing, child]);
    }, new Map<string, MerchantCategory[]>());

    // Sort parents alphabetically, children alphabetically within each group
    return [...parentMap.entries()]
      .toSorted(([, a], [, b]) => a.item_name.localeCompare(b.item_name))
      .map(([parentId, parent]) => ({
        parent,
        children: [...(childrenByParent.get(parentId) ?? [])].toSorted((a, b) =>
          a.item_name.localeCompare(b.item_name),
        ),
      }));
  }, [categories, relationships]);

  // Filter groups by typed text
  const filteredGroups = useMemo((): CategoryGroup[] => {
    if (!filter) return groups;
    const lower = filter.toLowerCase();
    return groups
      .map(g => {
        const parentMatches = g.parent.item_name.toLowerCase().includes(lower);
        // If parent matches, show all its children
        if (parentMatches) return g;
        // Otherwise only show matching children
        const matchingChildren = g.children.filter(c =>
          c.item_name.toLowerCase().includes(lower),
        );
        if (matchingChildren.length === 0) return null;
        return { ...g, children: matchingChildren };
      })
      .filter((g): g is CategoryGroup => g !== null);
  }, [groups, filter]);

  // Selected category display name
  const selectedName = useMemo(() => {
    if (!selectedCategoryId) return "";
    const cat = categories.find(c => c.id === selectedCategoryId);
    return cat?.item_name ?? "";
  }, [categories, selectedCategoryId]);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
        setFilter("");
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const selectCategory = (categoryId: string) => {
    const params = new URLSearchParams(searchParams.toString());
    if (categoryId) {
      params.set("category", categoryId);
    } else {
      params.delete("category");
    }
    const qs = params.toString();
    navigate(`/merchants${qs ? `?${qs}` : ""}`);
    setOpen(false);
    setFilter("");
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilter(e.target.value);
    if (!open) setOpen(true);
  };

  const handleInputFocus = () => {
    setOpen(true);
  };

  const handleClear = () => {
    selectCategory("");
    inputRef.current?.focus();
  };

  if (categories.length === 0) return null;

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="flex items-center rounded-pill border border-border-shell bg-surface-blank focus-within:border-accent focus-within:ring-2 focus-within:ring-accent/20">
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-controls={open ? "category-listbox" : undefined}
          value={open ? filter : selectedName}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={t("CategoryFilter.placeholder")}
          aria-label={t("CategoryFilter.placeholder")}
          className="w-full min-w-0 rounded-pill bg-transparent px-4 py-3 font-body text-text-full placeholder-text-light focus:outline-none"
        />
        {selectedCategoryId && !open && (
          <button
            type="button"
            onClick={handleClear}
            className="mr-2 shrink-0 rounded-full p-1 text-text-light hover:text-text-full"
            aria-label={t("CategoryFilter.clear")}
          >
            <svg
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
      </div>

      {open && (
        <ul
          id="category-listbox"
          role="listbox"
          className="absolute z-10 mt-1 max-h-72 w-full overflow-auto rounded-lg border border-border-shell bg-surface-blank py-1 shadow-content"
        >
          {filteredGroups.length === 0 && (
            <li className="px-4 py-2 text-sm text-text-light">
              {t("CategoryFilter.no-results")}
            </li>
          )}
          {filteredGroups.map(group => (
            <li key={group.parent.id}>
              <div className="px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-text-light">
                {group.parent.item_name}
              </div>
              <ul>
                {group.children.map(child => (
                  <li key={child.id}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={child.id === selectedCategoryId}
                      onClick={() => selectCategory(child.id)}
                      className={`w-full px-6 py-2 text-left text-sm hover:bg-surface-elevated ${
                        child.id === selectedCategoryId
                          ? "font-semibold text-accent"
                          : "text-text-full"
                      }`}
                    >
                      {child.item_name}
                    </button>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
